// TOEFL iBT Mock 4 — Writing (2026 format, advanced difficulty)
//
// 1. Build a Sentence – put the word tiles in order to complete a reply.
//                       Every item has TWO distractor tiles and targets an
//                       advanced structure ("little did I know", "were I in
//                       your position", "should you need", future perfect,
//                       "be used to + -ing", "had better", "whichever").
//                       `accept` lists other word orders that are also
//                       grammatical and use exactly the same tiles.
// 2. Write an Email   – 7 minutes, a formal correction request that must
//                       identify an error, show its consequences and propose
//                       a remedy.
// 3. Academic Discussion – 10 minutes, a two-part question: take a position
//                       AND say which workers it would not suit and what
//                       they should get instead.

export const TASK_SECONDS = {
  build: 6 * 60,
  email: 7 * 60,
  discussion: 10 * 60,
};

export const buildSentenceItems = [
  {
    id: 1,
    speaker: "Did you enjoy your summer internship?",
    prefix: "Yes, though at first",
    answer: ["little", "did", "I", "know", "how", "much", "I", "would", "learn", "there"],
    suffix: ".",
    distractors: ["knew", "will"],
  },
  {
    id: 2,
    speaker: "Should I accept the offer from the smaller company?",
    prefix: "Personally,",
    answer: ["were", "I", "in", "your", "position", "I", "would", "accept", "it", "immediately"],
    accept: [
      ["were", "I", "in", "your", "position", "I", "would", "immediately", "accept", "it"],
      ["I", "would", "accept", "it", "immediately", "were", "I", "in", "your", "position"],
      ["I", "would", "immediately", "accept", "it", "were", "I", "in", "your", "position"],
    ],
    suffix: ".",
    distractors: ["was", "will"],
  },
  {
    id: 3,
    speaker: "Who discovered the error in the dataset?",
    prefix: "Actually, it was",
    answer: ["the", "newest", "member", "of", "the", "team", "who", "noticed", "it", "first"],
    accept: [["the", "newest", "member", "of", "the", "team", "who", "first", "noticed", "it"]],
    suffix: ".",
    distractors: ["whom", "noticing"],
  },
  {
    id: 4,
    speaker: "Will the project be finished before the review?",
    prefix: "By the time the committee meets,",
    answer: ["we", "will", "have", "completed", "every", "stage", "of", "the", "analysis"],
    suffix: ".",
    distractors: ["had", "completing"],
  },
  {
    id: 5,
    speaker: "Is it all right if I submit the lab report a day late?",
    prefix: "Only if",
    answer: ["you", "can", "show", "that", "you", "were", "ill", "on", "the", "due", "date"],
    suffix: ".",
    distractors: ["was", "showing"],
  },
  {
    id: 6,
    speaker: "Why is Leah moving out of the dormitory?",
    prefix: "The reason",
    answer: ["she", "is", "leaving", "is", "that", "the", "rent", "has", "doubled"],
    suffix: ".",
    distractors: ["for", "doubling"],
  },
  {
    id: 7,
    speaker: "Do I need to bring anything to the workshop?",
    prefix: "Just your laptop, and",
    answer: ["should", "you", "need", "help", "just", "ask", "one", "of", "the", "assistants"],
    accept: [["just", "ask", "one", "of", "the", "assistants", "should", "you", "need", "help"]],
    suffix: ".",
    distractors: ["will", "needs"],
  },
  {
    id: 8,
    speaker: "Have you gotten used to the early morning classes yet?",
    prefix: "Not really.",
    answer: ["I'm", "still", "not", "used", "to", "waking", "up", "before", "seven"],
    suffix: ".",
    distractors: ["wake", "use"],
  },
  {
    id: 9,
    speaker: "Should we leave now or wait for the others?",
    prefix: "We",
    answer: ["had", "better", "leave", "now", "if", "we", "want", "to", "get", "good", "seats"],
    suffix: ".",
    distractors: ["leaving", "best"],
  },
  {
    id: 10,
    speaker: "What did the committee decide about the new elective courses?",
    prefix: "They agreed that",
    answer: ["whichever", "course", "attracts", "the", "most", "students", "will", "be", "offered", "again"],
    suffix: ".",
    distractors: ["whoever", "attract"],
  },
];

export const emailTask = {
  recipient: "Ms. Duarte",
  scenario:
    "Last week, the university newspaper published an article about student housing costs that quoted you. The article reported that you said the university \"does not care about students,\" but what you actually said was that the university had not yet responded to the housing survey. Since the article appeared, several staff members in the housing office have contacted you, and your student association is worried about its relationship with the administration. Write an email to Ms. Duarte, the newspaper's editor.",
  points: [
    "Explain what you were quoted as saying and what you actually said.",
    "Describe the consequences the misquotation has had.",
    "Request a specific correction and suggest how similar errors could be prevented.",
  ],
  // Words that show each bullet point was addressed (used by the scorer).
  pointKeywords: [
    ["quote", "quoted", "quotation", "misquote", "misquoted", "article", "reported", "said", "actually", "does not care", "doesn't care", "survey", "responded", "response", "words", "statement", "context", "interview", "never said", "did not say", "didn't say"],
    ["staff", "housing office", "contacted", "upset", "offended", "relationship", "administration", "association", "reputation", "trust", "damaged", "damage", "embarrassed", "embarrassing", "misunderstanding", "consequence", "consequences", "affected", "harm", "negotiation", "meeting"],
    ["correction", "correct", "retraction", "clarification", "clarify", "publish", "print", "online version", "update", "request", "ask", "would like", "prevent", "in the future", "future", "recording", "record", "check", "verify", "confirm", "review", "send quotes", "before publication", "suggest"],
  ],
  formal: true,
  minWords: 110,
  lengthSteps: [[150, 5], [125, 4.5], [105, 4], [85, 3], [65, 2], [40, 1], [1, 0.5]],
};

export const discussionTask = {
  course: "business management",
  professor: "Dr. Kowalczyk",
  question:
    "Several companies have recently tested a four-day workweek, in which employees work four days instead of five for the same salary. Many reported that productivity stayed the same or even improved, while employees said they were less stressed. Should more companies adopt a four-day workweek? In your post, also identify a type of job for which this schedule would not work well, and explain what those workers should receive instead.",
  posts: [
    {
      name: "Hana",
      text: "I think it's a great idea. When people know they only have four days, they waste less time in unnecessary meetings and focus on what really matters. Rested employees also make fewer mistakes and are less likely to quit, which saves companies money on hiring.",
    },
    {
      name: "Marco",
      text: "I'm skeptical. The trials mostly involved office workers. Hospitals, schools, and shops need to be open five or more days a week, so either they hire more staff, which is expensive, or employees squeeze five days of work into four longer, more exhausting days.",
    },
  ],
  topicKeywords: [
    "four-day", "four", "day", "days", "week", "workweek", "schedule", "hours", "work", "workers", "employee", "employees",
    "company", "companies", "employer", "employers", "productivity", "productive", "stress", "stressed", "burnout", "salary",
    "office", "meetings", "rest", "weekend", "balance", "staff", "shifts", "customers", "hospital", "hospitals", "nurses", "teachers",
  ],
  // The professor asks two things; each must be answered.
  questionParts: [
    {
      label: "Take a clear position on adopting a four-day workweek",
      keywords: ["in my view", "in my opinion", "i believe", "i think", "should adopt", "should not adopt", "shouldn't adopt", "should introduce", "four-day week", "four-day workweek", "support", "oppose", "in favor", "against"],
    },
    {
      label: "Identify a job it would not suit and say what those workers should receive instead",
      keywords: ["would not work", "wouldn't work", "does not work", "doesn't work", "not suitable", "unsuitable", "would not suit", "nurses", "doctors", "teachers", "drivers", "retail", "shop", "restaurant", "factory", "emergency", "instead", "receive", "compensation", "compensated", "extra pay", "bonus", "higher pay", "more vacation", "paid leave", "time off", "flexible", "rotating", "rotation"],
    },
  ],
  minWords: 120,
  lengthSteps: [[160, 5], [135, 4.5], [115, 4], [95, 3], [70, 2], [45, 1], [1, 0.5]],
};
